import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, AlertCircle, Grid3x3, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import cuttingPlanService from '../services/cuttingPlanService';
import positionService from '../services/positionService';
import partService from '../services/partService';
import toast from 'react-hot-toast';
import BoardCanvas from './BoardCanvas';
import BoardMetricsCard from './BoardMetricsCard';
import PositionFormModal from './PositionFormModal';
import LabelManager from './LabelManager';
import ConfirmModal from './ConfirmModal';

const BoardCutViewer = () => {
  const { planId, boardId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [plan, setPlan] = useState(null);
  const [board, setBoard] = useState(null);
  const [positions, setPositions] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });

  const canAccess = hasRole?.(['GERENCIA']) || hasRole?.(['RECEPCAO_COMPRAS_AGENDA']);

  const loadData = async () => {
    if (!canAccess) {
      setError('Você não tem permissão para acessar este recurso');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const planResponse = await cuttingPlanService.getPlanById(planId);
      if (!planResponse.success || !planResponse.data) {
        setError('Plano de corte não encontrado');
        setLoading(false);
        return;
      }
      setPlan(planResponse.data);

      const boardResponse = await cuttingPlanService.getBoardById(boardId);
      if (!boardResponse.success || !boardResponse.data) {
        setError('Chapa não encontrada');
        setLoading(false);
        return;
      }
      setBoard(boardResponse.data);

      const [positionsResponse, partsResponse] = await Promise.all([
        positionService.getPositionsByBoard(boardId),
        partService.getPartsByModule(boardResponse.data.module_id || 0).catch(() => ({ success: true, data: [] }))
      ]);

      if (positionsResponse.success && Array.isArray(positionsResponse.data)) {
        setPositions(positionsResponse.data);
      } else {
        setPositions([]);
      }

      if (partsResponse.success && Array.isArray(partsResponse.data)) {
        setParts(partsResponse.data);
      } else {
        setParts([]);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [planId, boardId, canAccess]);

  const handleAddPosition = () => {
    setEditingPosition(null);
    setShowForm(true);
  };

  const handleEditPosition = (position) => {
    setEditingPosition(position);
    setShowForm(true);
  };

  const handleDeletePosition = async (position) => {
    setConfirmState({
      isOpen: true,
      message: `Tem certeza que deseja remover a peça ${position.part_code} desta chapa?`,
      onConfirm: async () => {
        try {
          await positionService.deletePosition(position.id);
          toast.success('Posição removida com sucesso');
          loadData();
        } catch (err) {
          console.error('Erro ao deletar posição:', err);
          toast.error('Erro ao deletar posição');
        }
      }
    });
  };

  const handleConfirmClose = () => {
    setConfirmState({ isOpen: false, message: '', onConfirm: null });
  };

  const handleSavePosition = async (positionId, data) => {
    try {
      if (positionId) {
        await positionService.updatePosition(positionId, {
          positionX: data.positionX,
          positionY: data.positionY,
          rotation: data.rotation,
          cutSequence: data.cutSequence,
          cutStatus: data.cutStatus
        });
        toast.success('Posição atualizada com sucesso');
      } else {
        await positionService.createPosition(data);
        toast.success('Peça posicionada com sucesso');
      }
      setShowForm(false);
      setEditingPosition(null);
      loadData();
    } catch (err) {
      console.error('Erro ao salvar posição:', err);
      toast.error(err.message || 'Erro ao salvar posição');
      throw err;
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingPosition(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-16 bg-gray-200 rounded-lg"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-96 bg-gray-200 rounded-lg"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={() => navigate(`/cutting-plans/${planId}`)}
              className="text-red-600 hover:text-red-800 text-sm mt-1"
            >
              Voltar para o plano
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <nav className="flex items-center gap-2 text-sm text-gray-600 mb-4">
        <button
          onClick={() => navigate(`/cutting-plans/${planId}`)}
          className="flex items-center gap-1 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Planos de Corte
        </button>
        <span>/</span>
        <button
          onClick={() => navigate(`/cutting-plans/${planId}`)}
          className="hover:text-gray-900"
        >
          {plan?.plan_code}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium flex items-center gap-1">
          <Grid3x3 className="w-4 h-4" />
          {board?.board_number}
        </span>
      </nav>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Corte: {board?.board_number}
          </h1>
          <p className="text-gray-600 mt-1">
            {board?.width} x {board?.height}mm - {board?.material_name}
          </p>
        </div>
        <button
          onClick={handleAddPosition}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Posicionar Peça
        </button>
        <button
          onClick={() => setShowLabelManager(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Tag className="w-5 h-5" />
          Gerenciar Etiquetas
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <BoardCanvas
            board={board}
            positions={positions}
            onEditPosition={handleEditPosition}
            onDeletePosition={handleDeletePosition}
          />

          {positions.length > 0 && (
            <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Peças Posicionadas ({positions.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dimensões</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Posição</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rotação</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {positions.map((pos) => (
                      <tr key={pos.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="font-medium text-gray-900 text-sm">{pos.part_code}</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                          {pos.part_width} x {pos.part_height}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                          ({pos.position_x}, {pos.position_y})
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                          {pos.rotation}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            pos.cut_status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700' :
                            pos.cut_status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700' :
                            pos.cut_status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                            'bg-amber-50 text-amber-700'
                          }`}>
                            {pos.cut_status}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleEditPosition(pos)}
                            className="p-1 text-gray-500 hover:text-blue-600"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeletePosition(pos)}
                            className="p-1 text-gray-500 hover:text-red-600 ml-2"
                          >
                            Remover
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div>
          <BoardMetricsCard board={board} positions={positions} />

          <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Informações da Chapa
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Número:</dt>
                <dd className="font-medium text-gray-900">{board?.board_number}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Material:</dt>
                <dd className="font-medium text-gray-900">{board?.material_name || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Espessura:</dt>
                <dd className="font-medium text-gray-900">{board?.thickness ? `${board.thickness}mm` : '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Dimensões:</dt>
                <dd className="font-medium text-gray-900">{board?.width} x {board?.height}mm</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Status:</dt>
                <dd className="font-medium text-gray-900">{board?.status}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <PositionFormModal
        position={editingPosition}
        board={board}
        parts={parts}
        planId={planId}
        isOpen={showForm}
        onClose={handleCloseForm}
        onSave={handleSavePosition}
        onDelete={handleDeletePosition}
      />

      <LabelManager
        planId={parseInt(planId)}
        positions={positions}
        planCode={plan?.plan_code}
        projectId={plan?.project_id}
        isOpen={showLabelManager}
        onClose={() => setShowLabelManager(false)}
      />

      <ConfirmModal
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onClose={handleConfirmClose}
      />
    </div>
  );
};

export default BoardCutViewer;
